import React, { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { ISelectOption } from "../interfaces/shared/ISelectOption";
import debounce from "lodash/debounce";

interface AsyncSelect2Props<T> {
    name?: string;
    value?: T | null;
    onChange?: (value: T | null) => void;
    loadOptions: (inputValue: string) => Promise<ISelectOption<T>[]>;
    defaultOptions?: ISelectOption<T>[] | boolean;
    label?: string;
    placeholder?: string;
    isDisabled?: boolean;
    isClearable?: boolean;
    error?: string;
    cacheOptions?: boolean;
}

const customStyles = {
    control: (base: any, state: any) => ({
        ...base,
        borderRadius: "8px",
        padding: "0.35rem 0",
        borderColor: state.isFocused ? "#dbe0e5" : "#dbe0e5",
        boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(147, 7, 31, 0.25)" : "none",
        "&:hover": {
            borderColor: "#d21034",
        },
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
};

const AsyncSelect2 = <T,>({
    name,
    value: externalValue,
    onChange: externalOnChange,
    loadOptions,
    defaultOptions = true,
    label,
    placeholder,
    isDisabled = false,
    isClearable = true,
    error,
    cacheOptions = true,
}: AsyncSelect2Props<T>) => {
    const [selectedOption, setSelectedOption] = useState<ISelectOption<T> | null>(null);

    // Debounced load options to avoid too many API calls
    const debouncedLoadOptions = useCallback(
        debounce((inputValue: string, callback: (options: ISelectOption<T>[]) => void) => {
            loadOptions(inputValue).then(callback);
        }, 300),
        [loadOptions]
    );

    const handleChange = (selected: ISelectOption<T> | null) => {
        setSelectedOption(selected);
        externalOnChange?.(selected ? selected.value : null);
    };

    // Update selected option when external value changes
    useEffect(() => {
        if (externalValue === null || externalValue === undefined) {
            setSelectedOption(null);
        } else if (Array.isArray(defaultOptions)) {
            // Find matching option from defaultOptions
            const matchingOption = defaultOptions.find(opt => opt.value === externalValue);
            if (matchingOption) {
                setSelectedOption(matchingOption);
            }
        }
    }, [externalValue, defaultOptions]);

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label>{label}</Form.Label>}

            <AsyncSelect
                name={name}
                isDisabled={isDisabled}
                isClearable={isClearable}
                cacheOptions={cacheOptions}
                defaultOptions={defaultOptions}
                loadOptions={debouncedLoadOptions}
                value={selectedOption}
                placeholder={placeholder}
                styles={customStyles}
                menuPortalTarget={document.body}
                onChange={handleChange}
                noOptionsMessage={({ inputValue }) =>
                    inputValue ? "No results found" : "Start typing to search..."
                }
                loadingMessage={() => "Loading..."}
            />

            {error && <div className="invalid-feedback d-block">{error}</div>}
        </Form.Group>
    );
};

export default AsyncSelect2;
